/**
 * Change Log Slice
 * 
 * Manages change log cache for all exhibits (EX2-EX9)
 * Simple cache structure: each exhibit has its own array or null
 * Cache is cleared when fiscal year changes
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetReduxState } from '../actions';
import { ParsedChangeLogItem } from '../../services/changeLogService';

export interface ChangeLogState {
  // Cache for each exhibit - null means not fetched yet
  financialStatement: ParsedChangeLogItem[] | null;  // EX2
  hierarchy: ParsedChangeLogItem[] | null;           // EX3
  riskAssessment: ParsedChangeLogItem[] | null;      // EX4
  control: ParsedChangeLogItem[] | null;             // EX5
  gap: ParsedChangeLogItem[] | null;                 // EX6
  certification: ParsedChangeLogItem[] | null;       // EX7
  compliance: ParsedChangeLogItem[] | null;          // EX8
  auditPlan: ParsedChangeLogItem[] | null;           // EX9
}

const initialState: ChangeLogState = {
  financialStatement: null,
  hierarchy: null,
  riskAssessment: null,
  control: null,
  gap: null,
  certification: null,
  compliance: null,
  auditPlan: null,
};

const changeLogSlice = createSlice({
  name: 'changeLog',
  initialState,
  reducers: {
    // Set cache for Financial Statement (EX2)
    setFinancialStatementChanges: (
      state,
      action: PayloadAction<ParsedChangeLogItem[]>
    ) => {
      state.financialStatement = action.payload;
    },

    // Set cache for Hierarchy (EX3)
    setHierarchyChanges: (
      state,
      action: PayloadAction<ParsedChangeLogItem[]>
    ) => {
      state.hierarchy = action.payload;
    },

    // Set cache for Risk Assessment (EX4)
    setRiskAssessmentChanges: (
      state,
      action: PayloadAction<ParsedChangeLogItem[]>
    ) => {
      state.riskAssessment = action.payload;
    },

    // Set cache for Control Activities (EX5)
    setControlChanges: (
      state,
      action: PayloadAction<ParsedChangeLogItem[]>
    ) => {
      state.control = action.payload;
    },

    // Set cache for Gaps (EX6)
    setGapChanges: (
      state,
      action: PayloadAction<ParsedChangeLogItem[]>
    ) => {
      state.gap = action.payload;
    },

    // Set cache for Certification (EX7)
    setCertificationChanges: (
      state,
      action: PayloadAction<ParsedChangeLogItem[]>
    ) => {
      state.certification = action.payload;
    },

    // Set cache for COSO Compliance (EX8)
    setComplianceChanges: (
      state,
      action: PayloadAction<ParsedChangeLogItem[]>
    ) => {
      state.compliance = action.payload;
    },

    // Set cache for Audit Plan (EX9)
    setAuditPlanChanges: (
      state,
      action: PayloadAction<ParsedChangeLogItem[]>
    ) => {
      state.auditPlan = action.payload;
    },

    // Clear all cache - used when fiscal year changes
    clearChangeLogCache: (state) => {
      state.financialStatement = null;
      state.hierarchy = null;
      state.riskAssessment = null;
      state.control = null;
      state.gap = null;
      state.certification = null;
      state.compliance = null;
      state.auditPlan = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetReduxState, () => initialState);
  },
});

export const {
  setFinancialStatementChanges,
  setHierarchyChanges,
  setRiskAssessmentChanges,
  setControlChanges,
  setGapChanges,
  setCertificationChanges,
  setComplianceChanges,
  setAuditPlanChanges,
  clearChangeLogCache,
} = changeLogSlice.actions;

export default changeLogSlice.reducer;
